// parseRoute — a URL to a `Route`. Parse-don't-validate at the URL boundary:
// total, never throws. Every recognized shape maps to its destination; ANY
// unrecognized shape parses to `notFound` (a real 404), NOT a silent fallback
// to a real view — a standard web app tells you the address is wrong rather
// than pretending you asked for home (F48 v2 behavior change from v1).
//
// What this validates is URL SHAPE, not existence: ids are opaque pass-through
// strings (no brand, no "malformed id"), so any string in a path slot parses
// fine. Whether `mat_42` still exists / may be seen is decided downstream, where
// it already is (Centre's error slot, reconcileTabs) — a valid-shape path to a
// gone resource is NOT a 404 here (it degrades via reconcile), only an
// unrecognized SHAPE is.
//
// One refinement still degrades in place rather than 404-ing: an unrecognized
// library `filter` facet drops to unfiltered (the facet is a refinement of the
// library destination, not its identity). An unknown `/settings/<x>` section,
// by contrast, IS an unknown destination → notFound.

import {
    SEARCH_FACETS,
    type SearchFacet,
} from "../../components/Search/searchModel.js";
import {
    SETTINGS_SECTIONS,
    type SettingsSectionKind,
} from "../../components/Settings/types.js";
import { type Route, routeNewChat } from "./types.js";

// `.find` (equality, not `.includes`) so a raw `string`/`null` narrows to the
// closed union without a cast.
const asFacet = (value: string | null): SearchFacet | undefined =>
    SEARCH_FACETS.find((facet) => facet === value);

const asSection = (value: string): SettingsSectionKind | undefined =>
    SETTINGS_SECTIONS.find((section) => section === value);

// `decodeURIComponent` throws on a malformed escape (`/library/%E0`); keep the
// raw segment instead so parseRoute stays total (agent-written links reach it).
const decodeSegment = (segment: string): string => {
    try {
        return decodeURIComponent(segment);
    } catch {
        return segment;
    }
};

// Path segments, decoded, empties dropped ("/" ⇒ [], "/material/x" ⇒
// ["material","x"]).
const segmentsOf = (pathname: string): readonly string[] =>
    pathname
        .split("/")
        .filter((s) => s !== "")
        .map(decodeSegment);

const notFound = (path: string): Route => ({ type: "notFound", path });

export const parseRoute = (url: URL): Route => {
    const segments = segmentsOf(url.pathname);
    const q = url.searchParams;
    const path = url.pathname;

    if (segments.length === 0) return routeNewChat;

    const [head, second] = segments;
    const n = segments.length;
    switch (head) {
        case "journal":
            return n === 1 ? { type: "journal" } : notFound(path);
        case "studio":
            return n === 1 ? { type: "studio" } : notFound(path);
        case "projects":
            return n === 1
                ? { type: "projects" }
                : n === 2
                  ? { type: "studio", projectId: second }
                  : notFound(path);
        case "classic": {
            // The old Studio, hand-typed only (types.ts).
            const [, , third] = segments;
            if (second === "studio" && n === 2)
                return { type: "classicStudio" };
            return second === "projects" && third !== undefined && n === 3
                ? { type: "classicStudio", projectId: third }
                : notFound(path);
        }
        case "knowledge":
            return n <= 2
                ? { type: "knowledge", selected: second }
                : notFound(path);
        case "library":
            return n <= 2
                ? {
                      type: "library",
                      query: q.get("q") ?? "",
                      facet: asFacet(q.get("filter")),
                      selected: second,
                  }
                : notFound(path);
        case "c":
            return second !== undefined && n === 2
                ? { type: "conversation", conversationId: second }
                : notFound(path);
        case "source":
            // A source lives in the Knowledge bank (#59): its deep link is its
            // viewer there.
            return second !== undefined && n === 2
                ? { type: "knowledge", selected: second }
                : notFound(path);
        case "material":
            // A material lives in the library: its deep link is the library
            // with that row selected (B4), parsed to the one canonical route
            // (the material tab is retired, 2026-10-01).
            return second !== undefined && n === 2
                ? {
                      type: "library",
                      query: "",
                      facet: head,
                      selected: second,
                  }
                : notFound(path);
        case "package":
            return second !== undefined && n === 2
                ? { type: "package", id: second }
                : notFound(path);
        case "quality":
            return second !== undefined && n === 2
                ? { type: "quality", materialId: second }
                : notFound(path);
        case "settings": {
            // Bare `/settings` opens the default (first) tab.
            if (n === 1) return { type: "settings", section: "account" };
            if (second === undefined || n !== 2) return notFound(path);
            const section = asSection(second);
            return section === undefined
                ? notFound(path)
                : { type: "settings", section };
        }
        default:
            return notFound(path);
    }
};
